import React, { useState } from "react";
import { X, Search, Plus } from "lucide-react";
import { toast } from "react-toastify";

const SUGGESTED_SKILLS = [
  "UI Design",
  "UX Research",
  "Prototyping",
  "Figma",
  "Adobe XD",
  "Adobe XD",
  "Communication",
];

const MAX_SKILLS = 45;
const MIN_SKILL_LENGTH = 3;
const MAX_SKILL_LENGTH = 55;

const AddSkillModal = ({ skills, onClose, onSave }) => {
  const [draftSkills, setDraftSkills] = useState(skills);
  const [query, setQuery] = useState("");

  const remaining = Math.max(MAX_SKILLS - draftSkills.length, 0);
  const availableSuggestions = [...new Set(SUGGESTED_SKILLS)].filter(
    (skill) => !draftSkills.some((selected) => selected.toLowerCase() === skill.toLowerCase())
  );

  const addSkill = (skill) => {
    const trimmed = skill.trim();
    if (trimmed.length < MIN_SKILL_LENGTH || trimmed.length > MAX_SKILL_LENGTH) {
      toast.warn(`Skills must be ${MIN_SKILL_LENGTH} to ${MAX_SKILL_LENGTH} characters.`);
      return false;
    }
    if (draftSkills.some((selected) => selected.toLowerCase() === trimmed.toLowerCase()) || draftSkills.length >= MAX_SKILLS) return false;
    setDraftSkills((prev) => [...prev, trimmed]);
    return true;
  };

  const removeSkill = (skill) => {
    setDraftSkills((current) => current.filter((item) => item !== skill));
  };

  const handleQueryKeyDown = (e) => {
    if (e.key === "Enter" && query.trim()) {
      e.preventDefault();
      if (addSkill(query)) setQuery("");
    }
  };

  const handleSave = () => {
    const trimmed = query.trim();
    if (trimmed && (trimmed.length < MIN_SKILL_LENGTH || trimmed.length > MAX_SKILL_LENGTH)) {
      toast.warn(`Skills must be ${MIN_SKILL_LENGTH} to ${MAX_SKILL_LENGTH} characters.`);
      return;
    }
    const finalSkills =
      trimmed && !draftSkills.some((skill) => skill.toLowerCase() === trimmed.toLowerCase()) && draftSkills.length < MAX_SKILLS
        ? [...draftSkills, trimmed]
        : draftSkills;
    if (finalSkills.some((skill) => skill.trim().length < MIN_SKILL_LENGTH || skill.trim().length > MAX_SKILL_LENGTH)) {
      toast.warn(`Skills must be ${MIN_SKILL_LENGTH} to ${MAX_SKILL_LENGTH} characters.`);
      return;
    }
    onSave(finalSkills);
  };

  return (
    <div className="pm-modal-backdrop" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header d-flex align-items-center justify-content-between">
          <h3 className="pm-modal-title">Add Skill</h3>
          <button type="button" className="pm-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <label className="pm-field-label">Skills</label>
        <div className="pm-search-wrap">
          <Search size={16} className="pm-search-icon" />
          <input
            type="text"
            className="pm-input pm-search-input"
            placeholder="Skill(ex: UI/UX Designer)"
            value={query}
            minLength={MIN_SKILL_LENGTH}
            maxLength={MAX_SKILL_LENGTH}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleQueryKeyDown}
          />
        </div>
        <div className="pm-hint">You can add {remaining} more skills ({MIN_SKILL_LENGTH}-{MAX_SKILL_LENGTH} characters each)</div>

        {draftSkills.length > 0 && (
          <div className="pm-modal-selected mt-3" aria-live="polite">
            <div className="pm-suggest-title">Selected skills</div>
            <div className="pm-chip-list">
              {draftSkills.map((skill) => (
                <span key={skill} className="pm-chip pm-chip-selected">
                  <span>{skill}</span>
                  <button type="button" onClick={() => removeSkill(skill)} aria-label={`Remove ${skill}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {availableSuggestions.length > 0 && (
          <>
            <div className="pm-suggest-title mt-3">Suggested based on your profile</div>
            <div className="pm-chip-list">
              {availableSuggestions.map((skill) => (
              <button
                type="button"
                key={skill}
                className="pm-chip pm-chip-suggest"
                onClick={() => addSkill(skill)}
              >
                {skill} <Plus size={12} />
              </button>
              ))}
            </div>
          </>
        )}

        <button type="button" className="pm-save-btn w-100 mt-4" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default AddSkillModal;
